import React, { useState, useEffect, useRef } from 'react';
import { safeUrl } from '../lib/safeUrl';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { usePlanGate, planGateFromResponse, getPlanGate } from '../lib/planGate';
import PlanLimitDialog from '../components/dialogs/PlanLimitDialog';
import { useBillingDetails } from '../components/dialogs/BillingDetailsDialog';
import { getIndustryLabel } from '../constants/industries';
import IndustryMultiSelectFilter from '../components/IndustryMultiSelectFilter';
import AdminReportDialog from '../components/dialogs/AdminReportDialog';
import ShareLink from '../components/ShareLink';
import { toast } from 'sonner';
import { useOnboarding, OnboardingEmptyState } from '../onboarding';
import { 
  FileText, Search, Globe, Factory, Calendar, ChevronDown, X, 
  Download, Eye, ShoppingCart, Star, Clock, TrendingUp, MapPin,
  BarChart3, FileBarChart, Building, Zap, RefreshCw, Filter,
  BookOpen, Target, Award, CheckCircle, Plus, Pencil, Trash2, ArrowLeft, ArrowRight, Loader2,
  LayoutGrid, List
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const countryFlags = {
  SA: '🇸🇦', AE: '🇦🇪', OM: '🇴🇲', KW: '🇰🇼', BH: '🇧🇭', QA: '🇶🇦', GCC: '🌍'
};

const countryNames = {
  en: { SA: 'Saudi Arabia', AE: 'UAE', OM: 'Oman', KW: 'Kuwait', BH: 'Bahrain', QA: 'Qatar', GCC: 'GCC-Wide' },
  ar: { SA: 'السعودية', AE: 'الإمارات', OM: 'عمان', KW: 'الكويت', BH: 'البحرين', QA: 'قطر', GCC: 'دول الخليج' }
};

const reportTypeColors = {
  country: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  industry: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  thematic: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
  insight: 'bg-amber-500/10 text-amber-600 border-amber-500/30'
};

const reportTypeIcons = {
  country: Globe,
  industry: Factory,
  thematic: TrendingUp,
  insight: Zap
};

// WO-21 — the MOCK_REPORTS array that used to sit here (invented market
// reports with invented prices and page counts) was shown to the customer
// whenever the reports call did not return 200 — including a plan refusal.
// Deleted: a paid catalogue must never show products that do not exist.

// Multi-select filter component
const MultiSelectFilter = ({ label, icon: Icon, options, selected, onChange, language, isRTL, testId }) => {
  const [open, setOpen] = useState(false);
  
  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const clearAll = () => onChange([]);
  const selectAll = () => onChange(options.map(o => o.value));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className="h-11 px-3 justify-between min-w-[150px]"
          data-testid={testId}
        >
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-muted-foreground" />
            <span className="truncate max-w-[100px]">
              {selected.length === 0 
                ? label 
                : selected.length === 1 
                  ? options.find(o => o.value === selected[0])?.label || selected[0]
                  : `${selected.length} ${language === 'ar' ? 'محدد' : 'selected'}`
              }
            </span>
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <div className="p-2 border-b flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={selectAll} className="h-7 text-xs">
            {language === 'ar' ? 'تحديد الكل' : 'Select All'}
          </Button>
          <Button variant="ghost" size="sm" onClick={clearAll} className="h-7 text-xs">
            {language === 'ar' ? 'مسح' : 'Clear'}
          </Button>
        </div>
        <ScrollArea className="h-[250px]">
          <div className="p-2 space-y-1">
            {options.map((option) => (
              <div
                key={option.value}
                className={`flex items-center gap-2 px-2 py-2 rounded-md cursor-pointer hover:bg-muted transition-colors ${
                  selected.includes(option.value) ? 'bg-primary/10' : ''
                }`}
                onClick={() => toggleOption(option.value)}
              >
                <Checkbox
                  checked={selected.includes(option.value)}
                  onCheckedChange={() => toggleOption(option.value)}
                  className="pointer-events-none"
                />
                {option.icon && <span>{option.icon}</span>}
                <span className="text-sm flex-1">{option.label}</span>
              </div>
            ))}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default function ReportsPage() {
  const { language, isRTL } = useLanguage();
  const { user, token } = useAuth();
  const { entitlements } = useEntitlements();
  const planGate = usePlanGate();
  const { billingDialog, askBillingDetails } = useBillingDetails(); // WO-44
  const navigate = useNavigate();
  const { id: routeReportId } = useParams();
  const isAdmin = user?.role === 'admin';

  // WO-33 — opening the catalogue ticks checklist task E on the Free plan.
  const onb = useOnboarding();
  const { eligible: onbEligible, plan: onbPlan, markReportsOpened } = onb;
  const reportsOpenedSent = useRef(false);
  useEffect(() => {
    if (reportsOpenedSent.current || !onbEligible || onbPlan !== 'free') return;
    reportsOpenedSent.current = true;
    markReportsOpened();
  }, [onbEligible, onbPlan, markReportsOpened]);
  const [singleReport, setSingleReport] = useState(null);
  const [singleLoading, setSingleLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('reports_view_mode') || 'card');
  useEffect(() => { localStorage.setItem('reports_view_mode', viewMode); }, [viewMode]);
  const [purchasedReportIds, setPurchasedReportIds] = useState([]);
  const [purchaseLoadingId, setPurchaseLoadingId] = useState(null);
  const [redeemLoadingId, setRedeemLoadingId] = useState(null);
  // WO-03 item 1d — when Stripe is not configured the server cannot start a
  // checkout, so we hide the Purchase button instead of handing the customer a
  // failing button. Defaults to true so a slow /config check never flashes the
  // "coming soon" state for a working store.
  const [paymentsConfigured, setPaymentsConfigured] = useState(true);
  // WO-63 — white-label (Word) versions: what this company owns, and what an
  // Enterprise user may buy (report id -> price), from /api/whitelabel/summary.
  const [whiteLabel, setWhiteLabel] = useState({ eligible: false, owned: [], offers: {} });
  const [wlLoadingId, setWlLoadingId] = useState(null);
  const [userPlan, setUserPlan] = useState('free');
  const [reportDiscount, setReportDiscount] = useState(0);
  
  // Admin CRUD state
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [deleteReport, setDeleteReport] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Filters
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [showMyReportsOnly, setShowMyReportsOnly] = useState(false);

  const fetchReports = async (append = false) => {
    if (append) setLoadingMore(true); else setLoading(true);
    
    try {
      const currentPage = append ? page + 1 : 1;
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (selectedCountries.length === 1) params.append('country', selectedCountries[0]);
      if (selectedTypes.length === 1) params.append('type', selectedTypes[0]);
      if (selectedIndustries.length === 1) params.append('industry', selectedIndustries[0]);
      if (showFeaturedOnly) params.append('featured', 'true');
      if (showMyReportsOnly) params.append('owned', 'true');
      params.append('page', currentPage);
      params.append('page_size', '12');
      
      const response = await fetch(`${BACKEND_URL}/api/intelligence/reports?${params}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        let filteredReports = data.items || [];
        
        // Client-side filtering for multiple selections
        if (selectedCountries.length > 1) {
          filteredReports = filteredReports.filter(r => selectedCountries.includes(r.country));
        }
        if (selectedTypes.length > 1) {
          filteredReports = filteredReports.filter(r => selectedTypes.includes(r.type));
        }
        if (selectedIndustries.length > 1) {
          filteredReports = filteredReports.filter(r => 
            r.industries?.some(ind => selectedIndustries.includes(ind))
          );
        }
        
        if (append) {
          setReports(prev => [...prev, ...filteredReports]);
          setPage(currentPage);
        } else {
          setReports(filteredReports);
          setPage(1);
        }
        setTotal(data.total ?? filteredReports.length);
        setHasMore((data.page || currentPage) < (data.total_pages || 1));
      } else {
        // WO-21 — a response that is not 200 means we do not know the
        // catalogue. Show nothing and say so. This branch used to display the
        // invented MOCK_REPORTS list, which also fired on a plan refusal, so a
        // Free customer was shown reports that do not exist.
        if (!append) {
          setReports([]);
          setTotal(0);
        }
        setHasMore(false);
        toast.error(language === 'ar'
          ? 'تعذر تحميل التقارير. يرجى المحاولة مرة أخرى.'
          : 'Could not load the reports. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      // WO-21 — same reason as above: never invent a catalogue.
      if (!append) {
        setReports([]);
        setTotal(0);
      }
      setHasMore(false);
      toast.error(language === 'ar'
        ? 'تعذر تحميل التقارير. يرجى المحاولة مرة أخرى.'
        : 'Could not load the reports. Please try again.');
    } finally {
      if (append) setLoadingMore(false); else setLoading(false);
    }
  };

  const loadMore = () => fetchReports(true);

  // WO-03 item 1d — is a checkout even possible? Drives the Purchase vs
  // "Purchasing coming soon" control on each paid card.
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/payments/config`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d && typeof d.configured === 'boolean') setPaymentsConfigured(d.configured); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchReports(false);
  }, [selectedCountries, selectedTypes, selectedIndustries, search, showFeaturedOnly, showMyReportsOnly]);

  // Batch R1 §4 — open a single report by its own URL (/dashboard/reports/:id).
  useEffect(() => {
    if (!routeReportId) { setSingleReport(null); return; }
    setSingleLoading(true);
    fetch(`${BACKEND_URL}/api/intelligence/reports/${routeReportId}`,
      token ? { headers: { 'Authorization': `Bearer ${token}` } } : {})
      .then(r => (r.ok ? r.json() : null))
      .then(d => setSingleReport(d))
      .catch(() => setSingleReport(null))
      .finally(() => setSingleLoading(false));
  }, [routeReportId, token]);

  // Load purchased reports + plan discount info
  useEffect(() => {
    if (!token) return;
    fetch(`${BACKEND_URL}/api/payments/subscription-status`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        if (data.purchased_report_ids) setPurchasedReportIds(data.purchased_report_ids);
        const plan = data.plan_id || 'free';
        setUserPlan(plan);
        const discounts = { free: 0, standard: 30, premium: 50, enterprise: 50 };
        setReportDiscount(discounts[plan] || 0);
      })
      .catch(() => {});
  }, [token]);

  // WO-63 — reload whenever the library changes (a purchase or a free credit).
  useEffect(() => {
    if (!token) return;
    fetch(`${BACKEND_URL}/api/whitelabel/summary`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setWhiteLabel({ eligible: !!data.eligible, owned: data.owned || [], offers: data.offers || {} }); })
      .catch(() => {});
  }, [token, purchasedReportIds]);

  const handleBuyWhiteLabel = async (report) => {
    const billing = await askBillingDetails('whitelabel');  // WO-67: white-label stays companies-only in Managed Payments countries
    if (!billing) return;
    setWlLoadingId(report._id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/whitelabel/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ report_id: report._id, billing })
      });
      const raw = await res.text();
      let data = null;
      try { data = JSON.parse(raw); } catch { /* not JSON */ }
      if (!res.ok) {
        const pg = getPlanGate(data);
        if (planGate.show(pg)) return;
        const detail = data && data.detail;
        toast.error(typeof detail === 'string' ? detail : (language === 'ar'
          ? 'الدفع غير متاح مؤقتاً. لم يتم خصم أي مبلغ.'
          : 'Payments are temporarily unavailable. Nothing has been charged.'));
        return;
      }
      if (data && data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(language === 'ar'
        ? 'تعذّر الوصول إلى الخادم. لم يتم إجراء أي عملية دفع.'
        : "Couldn't reach the server — no payment was started. Please try again.");
    } finally {
      setWlLoadingId(null);
    }
  };

  const downloadWhiteLabel = async (report) => {
    setWlLoadingId(report._id);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/whitelabel/reports/${report._id}/docx`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) {
        let detail = '';
        try { detail = (await resp.json()).detail; } catch (_) {}
        toast.error(detail || (language === 'ar' ? 'تعذر تنزيل ملف وورد.' : 'Could not download the Word file.'));
        return;
      }
      const cd = resp.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = m ? m[1] : `${report.ref_id || report._id}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(language === 'ar' ? 'تعذّر الوصول إلى الخادم.' : 'Could not reach the server. No file was downloaded.');
    } finally {
      setWlLoadingId(null);
    }
  };

  // Claim one included free monthly report (no Stripe). Server refuses via 402
  // plan_gate (routed to the shared dialog) or a plain 400 shown as a message.
  const handleRedeemFree = async (report) => {
    setRedeemLoadingId(report._id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/payments/redeem-free-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ report_id: report._id })
      });
      if (!res.ok) {
        const pg = await planGateFromResponse(res);
        if (planGate.show(pg)) return;
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || (language === 'ar' ? 'تعذر استخدام التقرير المجاني' : 'Could not redeem the free report.'));
        return;
      }
      const data = await res.json();
      setPurchasedReportIds(prev => prev.includes(report._id) ? prev : [...prev, report._id]);
      toast.success(language === 'ar' ? 'تمت إضافة التقرير إلى مكتبتك باستخدام رصيد مجاني' : (data.message || 'Report added to your library using a free monthly credit.'));
      try { window.dispatchEvent(new Event('entitlements:refetch')); } catch (_) {}
      fetchReports(false);
    } catch (err) {
      // Server unreachable — reassure that nothing was spent (§3c).
      toast.error(language === 'ar'
        ? 'تعذّر الوصول إلى الخادم. لم يتم خصم أي رصيد من مخصّصك، حاول مرة أخرى.'
        : "Couldn't reach the server — nothing was taken from your allowance. Please try again.");
    } finally {
      setRedeemLoadingId(null);
    }
  };

  // Batch R1 §2 — download the full PDF of a report the caller OWNS. The route
  // itself checks ownership server-side (403 if not owned), so this is not
  // relying on the button being hidden.
  const downloadOwnedReport = async (report) => {
    if (report.has_pdf === false) {
      toast.info(language === 'ar'
        ? 'لا يوجد ملف مرفق بهذا التقرير بعد.'
        : 'No file is attached to this report yet.');
      return;
    }
    setDownloadingId(report._id);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/intelligence/reports/${report._id}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) {
        let detail = '';
        try { detail = (await resp.json()).detail; } catch (_) {}
        toast.error(detail || (language === 'ar' ? 'تعذر تنزيل هذا التقرير.' : 'Could not download this report.'));
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.ref_id || report._id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(language === 'ar'
        ? 'تعذّر الوصول إلى الخادم. لم يتم تنزيل أي ملف.'
        : 'Could not reach the server. No file was downloaded.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePurchaseReport = async (report) => {
    // WO-44 — billing country, company or individual, and company details first.
    const billing = await askBillingDetails('report');
    if (!billing) return;
    setPurchaseLoadingId(report._id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/payments/create-report-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ report_id: report._id, origin_url: window.location.origin, billing })
      });
      // WO-03 item 1a — read the body DEFENSIVELY. A 500 can arrive as plain
      // text (not JSON); parsing it before the res.ok check is what handed the
      // customer "Unexpected token 'I'…". Read text first, then try to parse.
      const raw = await res.text();
      let data = null;
      try { data = JSON.parse(raw); } catch { /* server did not send JSON */ }
      if (!res.ok) {
        const pg = getPlanGate(data);
        if (planGate.show(pg)) return;
        if (res.status === 429) {
          const retryAfter = res.headers.get('Retry-After');
          toast.error(language === 'ar'
            ? `عدد كبير من المحاولات.${retryAfter ? ` الرجاء الانتظار ${retryAfter} ثانية.` : ''}`
            : `Too many attempts.${retryAfter ? ` Please wait ${retryAfter} seconds.` : ''}`);
          return;
        }
        const detail = data && (data.detail || data.error);
        toast.error(typeof detail === 'string'
          ? detail
          : (language === 'ar'
              ? 'الدفع غير متاح مؤقتاً. لم يتم خصم أي مبلغ — يرجى المحاولة لاحقاً أو التواصل مع الدعم.'
              : 'Payments are temporarily unavailable. Nothing has been charged — please try again later or contact support.'));
        return;
      }
      if (data && data.url) window.location.href = data.url;
    } catch (err) {
      // Server unreachable — nothing was charged.
      toast.error(language === 'ar'
        ? 'تعذّر الوصول إلى الخادم. لم يتم إجراء أي عملية دفع، حاول مرة أخرى.'
        : "Couldn't reach the server — no payment was started. Please try again.");
    } finally {
      setPurchaseLoadingId(null);
    }
  };

  // Admin handlers
  const handleCreateReport = () => {
    setEditingReport(null);
    setShowReportDialog(true);
  };

  const handleEditReport = (report, e) => {
    e.stopPropagation();
    setEditingReport(report);
    setShowReportDialog(true);
  };

  const handleDeleteClick = (report, e) => {
    e.stopPropagation();
    setDeleteReport(report);
  };

  const handleConfirmDelete = async () => {
    if (!deleteReport) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/reports/${deleteReport._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete report');
      }
      
      toast.success(language === 'ar' ? 'تم حذف التقرير بنجاح' : 'Report deleted successfully');
      fetchReports();
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error(error.message);
    } finally {
      setIsDeleting(false);
      setDeleteReport(null);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const clearAllFilters = () => {
    setSelectedCountries([]);
    setSelectedTypes([]);
    setSelectedIndustries([]);
    setShowFeaturedOnly(false);
  };

  const hasActiveFilters = selectedCountries.length > 0 || selectedTypes.length > 0 || selectedIndustries.length > 0 || showFeaturedOnly;

  const translations = {
    en: {
      title: 'Market Reports',
      subtitle: 'Professional intelligence reports for strategic bidding decisions',
      searchPlaceholder: 'Search reports...',
      allCountries: 'All Countries',
      allTypes: 'Report Type',
      allIndustries: 'All Industries',
      country: 'Country Report',
      industry: 'Industry Report',
      thematic: 'Thematic Report',
      insight: 'Insight',
      freeInsight: 'Free Insight',
      viewInsight: 'View',
      downloadInsight: 'Download',
      downloadFreePdf: 'Download Free PDF',
      results: 'reports available',
      noReports: 'No reports found',
      noReportsDesc: 'Try adjusting your filters or check back later',
      preview: 'Preview',
      purchase: 'Purchase',
      download: 'Download',
      pages: 'pages',
      published: 'Published',
      refresh: 'Refresh',
      activeFilters: 'Active filters:',
      clearAll: 'Clear all',
      featured: 'Featured',
      viewAll: 'View All Reports',
      new: 'NEW',
      discount: 'SAVE',
      rating: 'rating',
      downloads: 'downloads',
      highlights: 'Key Highlights',
      structure: 'Report Structure',
      executiveSummary: 'Executive Summary',
      marketOverview: 'Market Overview',
      projectsLandscape: 'Projects & Tenders Landscape',
      keyParticipants: 'Key Market Participants',
      opportunityOutlook: 'Opportunity Outlook',
      recommendations: 'Strategic Recommendations',
      myReports: 'My Reports',
      browse: 'Browse Reports',
      createReport: 'Create Report',
      editReport: 'Edit',
      deleteReport: 'Delete',
      deleteConfirmTitle: 'Delete Report?',
      deleteConfirmDesc: 'This action cannot be undone. This will permanently delete the report.',
      cancel: 'Cancel',
      deleting: 'Deleting...'
    },
    ar: {
      title: 'تقارير السوق',
      subtitle: 'تقارير استخباراتية احترافية لقرارات المناقصات الاستراتيجية',
      searchPlaceholder: 'البحث في التقارير...',
      allCountries: 'جميع الدول',
      allTypes: 'نوع التقرير',
      allIndustries: 'جميع القطاعات',
      country: 'تقرير دولة',
      industry: 'تقرير قطاعي',
      thematic: 'تقرير موضوعي',
      insight: 'موجز',
      freeInsight: 'موجز مجاني',
      viewInsight: 'عرض',
      downloadInsight: 'تحميل',
      downloadFreePdf: 'تحميل PDF مجانًا',
      results: 'تقرير متاح',
      noReports: 'لا توجد تقارير',
      noReportsDesc: 'حاول تعديل عوامل التصفية أو تحقق لاحقاً',
      preview: 'معاينة',
      purchase: 'شراء',
      download: 'تحميل',
      pages: 'صفحة',
      published: 'تاريخ النشر',
      refresh: 'تحديث',
      activeFilters: 'الفلاتر النشطة:',
      clearAll: 'مسح الكل',
      featured: 'مميز',
      viewAll: 'عرض جميع التقارير',
      new: 'جديد',
      discount: 'وفر',
      rating: 'تقييم',
      downloads: 'تحميل',
      highlights: 'أبرز النقاط',
      structure: 'هيكل التقرير',
      executiveSummary: 'الملخص التنفيذي',
      marketOverview: 'نظرة عامة على السوق',
      projectsLandscape: 'مشهد المشاريع والمناقصات',
      keyParticipants: 'المشاركون الرئيسيون في السوق',
      opportunityOutlook: 'توقعات الفرص',
      recommendations: 'التوصيات الاستراتيجية',
      myReports: 'تقاريري',
      browse: 'تصفح التقارير',
      createReport: 'إنشاء تقرير',
      editReport: 'تعديل',
      deleteReport: 'حذف',
      deleteConfirmTitle: 'حذف التقرير؟',
      deleteConfirmDesc: 'لا يمكن التراجع عن هذا الإجراء. سيتم حذف التقرير نهائياً.',
      cancel: 'إلغاء',
      deleting: 'جارٍ الحذف...'
    }
  };

  const txt = translations[language];
  
  const typeOptions = [
    { value: 'insight', label: txt.insight },
    { value: 'country', label: txt.country },
    { value: 'industry', label: txt.industry },
    { value: 'thematic', label: txt.thematic }
  ];

  const ReportCard = ({ report, horizontal = false }) => {
    // Determine report type from 'type' field or derive from 'category'
    const getReportType = () => {
      if (report.type) return report.type;
      if (report.category) {
        if (report.category.toLowerCase().includes('country')) return 'country';
        if (report.category.toLowerCase().includes('industry')) return 'industry';
        return 'thematic';
      }
      return 'country'; // default
    };
    
    const reportType = getReportType();
    const TypeIcon = reportTypeIcons[reportType] || Globe;
    // Batch R1 — ownership from the server item, or the locally-tracked list
    // (updated instantly on redemption). Insight reports are free, not "owned".
    const owned = reportType !== 'insight' && (report.owned || purchasedReportIds.includes(report._id));
    const [industriesExpanded, setIndustriesExpanded] = useState(false);
    const [credit, setCredit] = useState(null);
    useEffect(() => {
      if (reportType === 'insight' || !token || purchasedReportIds.includes(report._id)) return;
      let active = true;
      fetch(`${BACKEND_URL}/api/payments/report-price/${report._id}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (active && d) setCredit(d); })
        .catch(() => {});
      return () => { active = false; };
    }, [report._id, purchasedReportIds]); // eslint-disable-line react-hooks/exhaustive-deps
    const discountPercent = report.discountedPrice && report.price 
      ? Math.round((1 - report.discountedPrice / report.price) * 100) 
      : 0;
    
    return (
      <Card 
        className={`group border-0 bg-card/50 backdrop-blur-sm hover:shadow-xl transition-all duration-300 overflow-hidden h-full flex flex-col ${horizontal ? 'md:flex-row' : ''}`}
        data-testid={`report-card-${report._id}`}
      >
        {/* Cover Image or Gradient Header */}
        <div className={`relative aspect-[15/7] shrink-0 overflow-hidden bg-gradient-to-br ${horizontal ? 'md:w-72 md:aspect-auto' : ''} ${
          reportType === 'insight'
            ? 'from-amber-400 to-amber-600'
            : (report.cover_file_id || report.coverImage)
              ? 'from-[#227821] to-blue-700'
              : reportType === 'country' ? 'from-blue-500 to-blue-700'
              : reportType === 'industry' ? 'from-emerald-500 to-emerald-700'
              : 'from-purple-500 to-purple-700'
        }`}>
          {(report.cover_file_id || report.coverImage) ? (
            <img
              src={report.cover_file_id
                ? `${BACKEND_URL}/api/intelligence/reports/${report._id}/cover`
                : report.coverImage}
              alt={report.title}
              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
              onError={e => { e.target.style.display = 'none'; }}
            />
          ) : null}
          {!(report.cover_file_id || report.coverImage) && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          )}
          <div className="absolute top-3 left-3 right-3 flex items-start justify-between">
            <div className="flex gap-2">
              {report.isNew && (
                <Badge className="bg-red-500 text-white border-0 text-xs">
                  {txt.new}
                </Badge>
              )}
              {(report.isFeatured || report.featured) && (
                <Badge className="bg-yellow-500 text-black border-0 text-xs">
                  <Star className="w-3 h-3 mr-1 fill-current" />
                  {txt.featured}
                </Badge>
              )}
              {report.bestseller && (
                <Badge className="bg-orange-500 text-white border-0 text-xs">
                  {language === 'ar' ? 'الأكثر مبيعاً' : 'Bestseller'}
                </Badge>
              )}
            </div>
            <Badge className="bg-black/40 text-white border-white/30 border text-xs backdrop-blur-sm">
              <TypeIcon className="w-3 h-3 mr-1" />
              {txt[reportType] || report.category}
            </Badge>
          </div>
          {!(report.cover_file_id || report.coverImage) && (
            <div className="absolute bottom-3 left-3">
              <div className="flex items-center gap-2 text-white text-sm">
                <span className="text-2xl">{countryFlags[report.country] || '🌍'}</span>
                <span className="font-medium">{countryNames[language][report.country] || report.country}</span>
              </div>
            </div>
          )}
        </div>

        <CardContent className="p-5 flex flex-col flex-1">
          {/* Ref ID Badge */}
          {report.ref_id && (
            <div className="mb-2">
              <Badge className="bg-primary text-primary-foreground font-mono text-xs" data-testid={`report-ref-id-${report._id}`}>
                {report.ref_id}
              </Badge>
            </div>
          )}
          
          {/* Title */}
          <h3 className={`text-lg font-bold mb-2 line-clamp-2 group-hover:text-primary transition-colors ${isRTL ? 'text-right' : ''}`}>
            {language === 'ar' ? (report.titleAr || report.title_ar || report.title) : report.title}
          </h3>

          {/* Summary */}
          <p className={`text-sm text-muted-foreground mb-4 line-clamp-2 ${isRTL ? 'text-right' : ''}`}>
            {language === 'ar' 
              ? (report.summaryAr || report.summary_ar || report.descriptionAr || report.description_ar || report.description) 
              : (report.summary || report.description)}
          </p>

          {/* Industries */}
          {report.industries?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4" data-testid={`report-industries-${report._id}`}>
              {(industriesExpanded ? report.industries : report.industries.slice(0, 3)).map((ind, i) => (
                <Badge key={i} variant="outline" className="text-xs">
                  {getIndustryLabel(ind, language)}
                </Badge>
              ))}
              {report.industries.length > 3 && (
                <Badge
                  variant="outline"
                  className="text-xs cursor-pointer bg-primary/5 border-primary/30 text-primary hover:bg-primary/10 transition-colors"
                  onClick={() => setIndustriesExpanded(v => !v)}
                  data-testid={`report-industries-toggle-${report._id}`}
                >
                  {industriesExpanded
                    ? (language === 'ar' ? 'عرض أقل' : 'Show less')
                    : `+${report.industries.length - 3} ${language === 'ar' ? 'المزيد' : 'more'}`}
                </Badge>
              )}
            </div>
          )}

          {/* Stats Row */}
          <div className={`flex items-center gap-4 text-xs text-muted-foreground mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="flex items-center gap-1">
              <FileText className="w-3.5 h-3.5" />
              <span>{report.pages || report.pageCount || 0} {txt.pages}</span>
            </div>
            {(report.rating || report.rating === 0) && (
              <div className="flex items-center gap-1">
                <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                <span>{report.rating}</span>
              </div>
            )}
            {(report.downloads || report.downloads === 0) && (
              <div className="flex items-center gap-1">
                <Download className="w-3.5 h-3.5" />
                <span>{report.downloads}</span>
              </div>
            )}
          </div>

          {/* Highlights */}
          {report.highlights && report.highlights.length > 0 && (
            <div className="mb-4 p-3 bg-muted/30 rounded-lg">
              <div className={`text-xs font-semibold mb-2 ${isRTL ? 'text-right' : ''}`}>
                {txt.highlights}:
              </div>
              <ul className={`space-y-1 ${isRTL ? 'text-right' : ''}`}>
                {report.highlights.slice(0, 3).map((highlight, i) => (
                  <li key={i} className={`text-xs text-muted-foreground flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <CheckCircle className="w-3 h-3 text-emerald-500 shrink-0" />
                    {highlight}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Spacer to push bottom content down */}
          <div className="flex-1"></div>

          {/* Cross-sell banner for Insights (if related_report_id present) */}
          {reportType === 'insight' && report.related_report_id && (() => {
            const related = reports.find((r) => r._id === report.related_report_id);
            if (!related) return null;
            return (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const el = document.querySelector(`[data-testid="report-card-${related._id}"]`);
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
                    setTimeout(() => el.classList.remove('ring-2', 'ring-primary', 'ring-offset-2'), 2000);
                  }
                }}
                className={`mb-3 w-full flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 hover:bg-primary/10 px-3 py-2 text-left transition-colors ${isRTL ? 'flex-row-reverse text-right' : ''}`}
                data-testid={`insight-cross-sell-${report._id}`}
              >
                <TrendingUp className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="text-xs leading-snug flex-1 min-w-0 truncate">
                  <span className="font-semibold text-primary">
                    {language === 'ar' ? 'للمزيد: ' : 'Dive deeper: '}
                  </span>
                  {language === 'ar'
                    ? (related.titleAr || related.title_ar || related.title)
                    : related.title}
                </span>
                <ArrowRight className={`w-3.5 h-3.5 text-primary shrink-0 ${isRTL ? 'rotate-180' : ''}`} />
              </button>
            );
          })()}

          {/* Price (paid) or Free badge (insight) */}
          <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {reportType === 'insight' ? (
                <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 text-sm px-2.5 py-1">
                  <Zap className="w-3.5 h-3.5 mr-1" />
                  {txt.freeInsight}
                </Badge>
              ) : reportDiscount > 0 ? (
                <>
                  <span className="text-2xl font-bold text-primary">
                    ${Math.round(report.price * (1 - reportDiscount / 100))}
                  </span>
                  <span className="text-sm text-muted-foreground line-through">${report.price}</span>
                  <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-xs">
                    {reportDiscount}% off
                  </Badge>
                </>
              ) : (
                <span className="text-2xl font-bold text-primary">${report.price}</span>
              )}
              {reportType !== 'insight' && (
                <span className="text-xs text-muted-foreground" data-testid={`report-excl-vat-${report._id}`}>
                  {language === 'ar' ? 'غير شامل الضريبة' : 'excl. VAT'}
                </span>
              )}
            </div>
          </div>

          {/* Publish Date */}
          <div className={`text-xs text-muted-foreground mb-4 flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Calendar className="w-3.5 h-3.5" />
            {txt.published}: {new Date(report.publishDate).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')}
          </div>

          {/* Actions - Always at bottom */}
          <div className="space-y-2 mt-auto">
            {/* Row with admin icons on left and share icons on right */}
            <div className="flex items-center justify-between mb-2">
              {isAdmin ? (
                <div className="flex gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-muted-foreground hover:text-primary h-8 w-8 p-0"
                    onClick={(e) => handleEditReport(report, e)}
                    data-testid={`edit-report-${report._id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-muted-foreground hover:text-destructive h-8 w-8 p-0"
                    onClick={(e) => handleDeleteClick(report, e)}
                    data-testid={`delete-report-${report._id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div></div>
              )}
              <ShareLink path={`/dashboard/reports/${report._id}`} title={report.title} compact />
            </div>
            {reportType === 'insight' ? (
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-10 border-emerald-600 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950"
                  onClick={() => {
                    const base = `${BACKEND_URL}/api/intelligence/reports/${report._id}/pdf?inline=1`;
                    const fallback = `${BACKEND_URL}/api/intelligence/reports/${report._id}/insight.pdf`;
                    const url = report.pdf_file_id
                      ? base
                      : (report.pdf_url || fallback);
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                  data-testid={`view-insight-${report._id}`}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {txt.viewInsight}
                </Button>
                <Button
                  size="sm"
                  className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => {
                    const uploaded = `${BACKEND_URL}/api/intelligence/reports/${report._id}/pdf`;
                    const generated = `${BACKEND_URL}/api/intelligence/reports/${report._id}/insight.pdf`;
                    const url = report.pdf_file_id
                      ? uploaded
                      : (report.pdf_url || generated);
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                  data-testid={`download-insight-${report._id}`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {txt.downloadInsight}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {owned && (
                  <Badge
                    className="bg-emerald-600 hover:bg-emerald-600 text-white gap-1"
                    data-testid={`owned-badge-${report._id}`}
                  >
                    <CheckCircle className="w-3 h-3" />
                    {language === 'ar' ? 'في مكتبتك' : 'In your library'}
                  </Badge>
                )}
                {credit?.can_redeem_free && !owned && (
                  <Button
                    className="w-full h-10 bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={redeemLoadingId === report._id}
                    onClick={() => handleRedeemFree(report)}
                    data-testid={`redeem-free-${report._id}`}
                  >
                    {redeemLoadingId === report._id
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <Zap className="w-4 h-4 mr-2" />}
                    {language === 'ar'
                      ? `استخدم تقريرك المجاني الشهري (${credit.free_reports_remaining} متبقٍ)`
                      : `Use your free monthly report (${credit.free_reports_remaining} left)`}
                  </Button>
                )}
                <div className="grid grid-cols-2 gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="h-10"
                  onClick={async () => {
                    if (report.preview_pdf_file_id) {
                      try {
                        const resp = await fetch(
                          `${BACKEND_URL}/api/intelligence/reports/${report._id}/preview`,
                          { headers: { 'Authorization': `Bearer ${token}` } }
                        );
                        if (!resp.ok) throw new Error('preview unavailable');
                        const blob = await resp.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        window.open(blobUrl, '_blank', 'noopener,noreferrer');
                        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                      } catch {
                        toast.error(language === 'ar' ? 'المعاينة غير متاحة' : 'Preview not available');
                      }
                    } else if (safeUrl(report.source_url)) {
                      window.open(safeUrl(report.source_url), '_blank', 'noopener,noreferrer');
                    } else {
                      toast.info(language === 'ar' ? 'المعاينة غير متاحة لهذا التقرير' : 'Preview not available for this report');
                    }
                  }}
                  data-testid={`preview-${report._id}`}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {txt.preview}
                </Button>
                {owned ? (
                  <Button
                    size="sm"
                    className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={downloadingId === report._id || report.has_pdf === false}
                    onClick={() => downloadOwnedReport(report)}
                    data-testid={`download-owned-${report._id}`}
                  >
                    {downloadingId === report._id
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <Download className="w-4 h-4 mr-2" />}
                    {report.has_pdf === false
                      ? (language === 'ar' ? 'لا يوجد ملف' : 'No file attached')
                      : txt.download}
                  </Button>
                ) : !paymentsConfigured ? (
                  <Button
                    size="sm"
                    className="h-10"
                    variant="secondary"
                    disabled
                    data-testid={`purchase-coming-soon-${report._id}`}
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    {language === 'ar' ? 'الشراء قريباً' : 'Purchasing coming soon'}
                  </Button>
                ) : (
                  <Button 
                    size="sm"
                    className="h-10 bg-primary hover:bg-primary/90"
                    disabled={purchaseLoadingId === report._id}
                    onClick={() => handlePurchaseReport(report)}
                    data-testid={`purchase-${report._id}`}
                  >
                    {purchaseLoadingId === report._id
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{language === 'ar' ? 'جارٍ...' : 'Loading...'}</>
                      : <><ShoppingCart className="w-4 h-4 mr-2" />{txt.purchase}</>}
                  </Button>
                )}
              </div>
              {/* WO-63 — white-label Word version */}
              {owned && whiteLabel.owned.includes(report._id) ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-10 border-blue-600 text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950"
                  disabled={wlLoadingId === report._id}
                  onClick={() => downloadWhiteLabel(report)}
                  data-testid={`download-whitelabel-${report._id}`}
                >
                  {wlLoadingId === report._id
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <FileText className="w-4 h-4 mr-2" />}
                  {language === 'ar' ? 'تنزيل نسخة وورد بالعلامة البيضاء' : 'Download white-label Word'}
                </Button>
              ) : owned && whiteLabel.eligible && whiteLabel.offers[report._id] != null && paymentsConfigured ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-10"
                  disabled={wlLoadingId === report._id}
                  onClick={() => handleBuyWhiteLabel(report)}
                  title={language === 'ar'
                    ? 'ملف وورد قابل للتعديل بدون أي علامة لـ Mid2Bid، لتضع علامتك التجارية عليه'
                    : 'An editable Word file with no Mid2Bid branding, ready for your own brand'}
                  data-testid={`buy-whitelabel-${report._id}`}
                >
                  {wlLoadingId === report._id
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <FileText className="w-4 h-4 mr-2" />}
                  {language === 'ar'
                    ? `نسخة بالعلامة البيضاء (وورد) — $${Number(whiteLabel.offers[report._id]).toFixed(2)}`
                    : `White-label Word version — $${Number(whiteLabel.offers[report._id]).toFixed(2)}`}
                </Button>
              ) : null}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Featured reports section (use real DB reports, not mocks)
  const featuredReports = reports.filter(r => r.isFeatured || r.featured);

  // Batch R1 §4 — a shared link (/dashboard/reports/:id) opens THAT report on its
  // own, not the whole catalogue.
  if (routeReportId) {
    return (
      <div className="p-6 space-y-6" data-testid="single-report-view">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard/reports')}
          className="flex items-center gap-2"
          data-testid="back-to-reports"
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'ar' ? 'كل التقارير' : 'All reports'}
        </Button>
        {singleLoading ? (
          <Card className="border-0 bg-card/50 overflow-hidden max-w-md">
            <Skeleton className="h-32 w-full" />
            <CardContent className="p-5 space-y-4">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        ) : singleReport ? (
          <div className="max-w-md">
            <ReportCard report={singleReport} />
          </div>
        ) : (
          <Card className="border-0 bg-card/50">
            <CardContent className="p-12 text-center" data-testid="single-report-not-found">
              <FileBarChart className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-xl font-semibold mb-2">
                {language === 'ar' ? 'التقرير غير موجود' : 'Report not found'}
              </h3>
              <p className="text-muted-foreground">
                {language === 'ar'
                  ? 'قد يكون هذا التقرير قد أُزيل أو أن الرابط غير صحيح.'
                  : 'This report may have been removed, or the link is incorrect.'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${isRTL ? 'font-arabic' : ''}`} data-testid="reports-page">
      {/* Back to Dashboard Button */}
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => navigate('/dashboard')}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
        data-testid="back-to-dashboard-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        {language === 'ar' ? 'العودة إلى لوحة التحكم' : 'Back to Dashboard'}
      </Button>

      {/* Header */}
      <div className={`flex flex-col md:flex-row md:items-center justify-between gap-4 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
        <div className={isRTL ? 'text-right' : ''}>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg">
              <FileBarChart className="w-7 h-7 text-white" />
            </div>
            {txt.title}
          </h1>
          <p className="text-muted-foreground mt-2">{txt.subtitle}</p>
        </div>
        <div className="flex flex-wrap gap-2" data-wo40="header-actions">
          {isAdmin && (
            <Button 
              onClick={handleCreateReport}
              className="flex items-center gap-2 bg-primary hover:bg-primary/90"
              data-testid="create-report-btn"
            >
              <Plus className="w-4 h-4" />
              {txt.createReport}
            </Button>
          )}
          <Button 
            variant="outline" 
            onClick={() => fetchReports(false)}
            className="flex items-center gap-2"
            data-testid="refresh-reports-btn"
          >
            <RefreshCw className="w-4 h-4" />
            {txt.refresh}
          </Button>
          <Button 
            variant={showMyReportsOnly ? 'default' : 'outline'}
            onClick={() => setShowMyReportsOnly(v => !v)}
            className="flex items-center gap-2"
            data-testid="my-reports-btn"
          >
            <BookOpen className="w-4 h-4" />
            {txt.myReports}
          </Button>
          {/* View mode toggle */}
          <div className="flex items-center border rounded-md overflow-hidden">
            <Button
              variant={viewMode === 'card' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none h-9 px-3"
              onClick={() => setViewMode('card')}
              data-testid="reports-view-card-btn"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none h-9 px-3"
              onClick={() => setViewMode('list')}
              data-testid="reports-view-list-btn"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Free monthly report credit — only for plans that include credits */}
      {(() => {
        const u = entitlements?.usage?.free_reports_per_month;
        if (!u || u.limit === 0) return null;
        const rem = u.remaining;
        return (
          <div className="rounded-lg border bg-emerald-500/10 border-emerald-500/30 px-4 py-3 text-sm flex items-center gap-2" data-testid="free-reports-credit-banner">
            <Zap className="w-4 h-4 text-emerald-600 shrink-0" />
            {language === 'ar'
              ? `لديك ${rem === -1 ? 'عدد غير محدود من' : rem} من التقارير المجانية المتبقية هذا الشهر`
              : `You have ${rem === -1 ? 'unlimited' : rem} free ${rem === 1 ? 'report' : 'reports'} left this month`}
          </div>
        );
      })()}

      {/* Filters */}
      <Card className="border-0 bg-card/50 backdrop-blur-sm">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className={`flex flex-col md:flex-row gap-3 flex-wrap ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            <div className="relative flex-1 min-w-[200px]">
              <Search className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-muted-foreground`} />
              <Input
                type="text"
                placeholder={txt.searchPlaceholder}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={`${isRTL ? 'pr-10' : 'pl-10'} h-11`}
                data-testid="reports-search-input"
              />
            </div>
            
            {/* Report Type Filter */}
            <MultiSelectFilter
              label={txt.allTypes}
              icon={FileText}
              options={typeOptions}
              selected={selectedTypes}
              onChange={(values) => setSelectedTypes(values)}
              language={language}
              isRTL={isRTL}
              testId="reports-type-filter"
            />
            
            {/* Country Filter */}
            <MultiSelectFilter
              label={txt.allCountries}
              icon={Globe}
              options={Object.keys(countryFlags).map(code => ({
                value: code,
                label: countryNames[language][code],
                icon: countryFlags[code]
              }))}
              selected={selectedCountries}
              onChange={(values) => setSelectedCountries(values)}
              language={language}
              isRTL={isRTL}
              testId="reports-country-filter"
            />
            
            {/* Multi-select Industry Filter (grouped by category) */}
            <IndustryMultiSelectFilter
              label={txt.allIndustries}
              icon={Factory}
              selected={selectedIndustries}
              onChange={(values) => setSelectedIndustries(values)}
              language={language}
              isRTL={isRTL}
              testId="reports-industry-filter"
            />
            
            {/* Featured Toggle */}
            <Button 
              type="button"
              variant={showFeaturedOnly ? "default" : "outline"}
              className="h-11"
              onClick={() => setShowFeaturedOnly(!showFeaturedOnly)}
              data-testid="reports-featured-filter"
            >
              <Star className={`w-4 h-4 mr-2 ${showFeaturedOnly ? 'fill-current' : ''}`} />
              {txt.featured}
            </Button>
            
            <Button type="submit" className="bg-primary hover:bg-primary/90 h-11" data-testid="reports-search-btn">
              <Search className="w-4 h-4 mr-2" />
              {language === 'ar' ? 'بحث' : 'Search'}
            </Button>
          </form>
          
          {/* Active filters display */}
          {hasActiveFilters && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50 flex-wrap">
              <span className="text-sm text-muted-foreground">{txt.activeFilters}</span>
              {selectedTypes.map(type => (
                <Badge key={type} variant="secondary" className="gap-1">
                  <FileText className="w-3 h-3" /> {txt[type]}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedTypes(prev => prev.filter(t => t !== type))} />
                </Badge>
              ))}
              {selectedCountries.map(code => (
                <Badge key={code} variant="secondary" className="gap-1">
                  {countryFlags[code]} {countryNames[language][code]}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedCountries(prev => prev.filter(c => c !== code))} />
                </Badge>
              ))}
              {selectedIndustries.map(industry => (
                <Badge key={industry} variant="secondary" className="gap-1">
                  <Factory className="w-3 h-3" /> {getIndustryLabel(industry, language)}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedIndustries(prev => prev.filter(i => i !== industry))} />
                </Badge>
              ))}
              {showFeaturedOnly && (
                <Badge variant="secondary" className="gap-1">
                  <Star className="w-3 h-3 fill-current" /> {txt.featured}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setShowFeaturedOnly(false)} />
                </Badge>
              )}
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs h-7">
                {txt.clearAll}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results count */}
      {!loading && (
        <div className={`text-sm text-muted-foreground ${isRTL ? 'text-right' : ''}`}>
          {showMyReportsOnly
            ? (language === 'ar'
                ? `عرض ${reports.length} من أصل ${total} من تقاريرك`
                : `Showing ${reports.length} of ${total} ${total === 1 ? 'report' : 'reports'} you own`)
            : (language === 'ar'
                ? `عرض ${reports.length} من ${total} ${txt.results}`
                : `Showing ${reports.length} of ${total} ${txt.results}`)}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="border-0 bg-card/50 overflow-hidden">
              <Skeleton className="h-32 w-full" />
              <CardContent className="p-5 space-y-4">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <div className="flex gap-2">
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-16" />
                </div>
                <Skeleton className="h-20 w-full" />
                <div className="grid grid-cols-2 gap-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : reports.length === 0 ? (
        /* WO-33 — helpful empty screens (onboarding switch on); the old card otherwise. */
        <OnboardingEmptyState
          kind={(hasActiveFilters || !!search) ? 'noResults' : 'myReports'}
          when={hasActiveFilters || !!search || showMyReportsOnly}
          onClear={() => { clearAllFilters(); setSearch(''); setSearchInput(''); }}
          onPrimary={() => setShowMyReportsOnly(false)}
        >
        <Card className="border-0 bg-card/50">
          <CardContent className="p-12 text-center" data-testid="reports-empty-state">
            <FileBarChart className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            {showMyReportsOnly ? (
              <>
                <h3 className="text-xl font-semibold mb-2">
                  {language === 'ar' ? 'مكتبتك فارغة' : 'Your library is empty'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'ar'
                    ? 'التقارير التي تشتريها أو تستبدلها برصيد مجاني ستظهر هنا.'
                    : 'Reports you buy — or redeem with a free monthly credit — will appear here.'}
                </p>
              </>
            ) : (
              <>
                <h3 className="text-xl font-semibold mb-2">{txt.noReports}</h3>
                <p className="text-muted-foreground">{txt.noReportsDesc}</p>
              </>
            )}
          </CardContent>
        </Card>
        </OnboardingEmptyState>
      ) : (
        <>
          <div className={viewMode === 'list' ? 'flex flex-col gap-4' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'}>
            {reports.map((report) => (
              <ReportCard key={report._id} report={report} horizontal={viewMode === 'list'} />
            ))}
          </div>

          {/* Load More Button */}
          {hasMore && reports.length > 0 && (
            <div className="flex justify-center pt-8">
              <Button
                variant="outline"
                size="lg"
                onClick={loadMore}
                disabled={loadingMore}
                className="min-w-[200px]"
                data-testid="load-more-reports"
              >
                {loadingMore ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-2" />
                    {language === 'ar' ? 'تحميل المزيد من التقارير' : 'Load More Reports'}
                  </>
                )}
              </Button>
            </div>
          )}

          {/* No more items message */}
          {!hasMore && reports.length > 0 && (
            <div className="text-center pt-8 text-muted-foreground">
              <p>{language === 'ar' ? 'لا يوجد المزيد من التقارير' : 'No more reports to load'}</p>
            </div>
          )}
        </>
      )}
      
      {/* Admin Report Dialog */}
      {billingDialog}
      <PlanLimitDialog open={planGate.open} onOpenChange={planGate.setOpen} gate={planGate.gate} message={planGate.message} />
      <AdminReportDialog
        open={showReportDialog}
        onOpenChange={setShowReportDialog}
        report={editingReport}
        token={token}
        language={language}
        onSuccess={fetchReports}
      />
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteReport} onOpenChange={(open) => !open && setDeleteReport(null)}>
        <AlertDialogContent data-testid="delete-report-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{txt.deleteConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {txt.deleteConfirmDesc}
              {deleteReport && (
                <span className="block mt-2 font-semibold text-foreground">
                  "{deleteReport.title}"
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting} data-testid="cancel-delete-report">
              {txt.cancel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-report"
            >
              {isDeleting ? txt.deleting : txt.deleteReport}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
